import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigation } from '@react-navigation/native';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { useIsCurrentUserEmployee } from '../../auth/hooks/useIsCurrentUserEmployee';
import { deleteMyAccount, logout, type CloseBusinessReason } from '../../auth/application/authSession';
import { getOfflineAccountSnapshot } from '../../shell/session/offlineAccountCache';
import { navigateInShell } from '../../shell/navigation/rootNavigation';
import { useScreenLoadShell } from '../../shared/ui/hooks-screen/useScreenLoad';
import { showError, showSuccess } from '../../shared/ui/appToast';
import {
  resolveDeleteAccountBlocked,
  resolveCloseBusinessBlockedDescription,
  resolveRevokeEmployeeBlockedDescription,
  type DeleteAccountPolicyInput,
} from './profileScreen.utils';
import { useImpersonationStore } from '../../shell/support/useImpersonationStore';
import { supabase } from '../../shared/supabase/supabaseClient';
import {
  fetchMyOrganizationTrustStatus,
  isTrustAttentionStatus,
  trustStatusTitle,
  type OrgTrustStatus,
} from '../infra/orgTrustStatusService';
import { MODULES } from '../../team/application/employeesApi';
import { buildEffectivePermissionSummary } from '../../team/screens/admin/employeePermissions/permissionUiLabels';
import { useEffectiveBusinessCapabilities } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { useOwnerPanelAccess } from '../../owner-panel/hooks/useOwnerPanelAccess';
import { returnToPostLoginHubPanel } from '../../post-login-hub/navigation/returnToPostLoginHub';
import { moduleAllowedByCapabilities } from '../../shell/feature-flags/businessCapabilitiesService';
import { useGlobalFeatureFlags } from '../../shell/providers/GlobalFeatureFlagsProvider';
import { isGlobalFeatureOn } from '../../shell/feature-flags/globalFeatureFlagsService';
import { planAllowsCustomer, planAllowsPromotions } from '../../shell/feature-flags/planEntitlementsService';

export function useProfileScreen() {
  const impersonating = useImpersonationStore((s) => s.context != null);
  const navigation = useNavigation<any>();
  const session = useAuthStore((state) => state.session);
  const branches = useSessionStore((state) => state.branches);
  const currentBranchId = useSessionStore((state) => state.currentBranchId);
  const isEmployeeRow = useIsCurrentUserEmployee();
  const { entitlements, loading: entLoading, refresh: refreshEntitlements } = usePlanEntitlements();
  const { flags: globalFeatureFlags } = useGlobalFeatureFlags();
  const effectiveCapabilities = useEffectiveBusinessCapabilities();
  const { canAccessOwnerPanel } = useOwnerPanelAccess();
  const [orgTrust, setOrgTrust] = useState<OrgTrustStatus | null>(null);

  const refreshOrgTrust = useCallback(async () => {
    setOrgTrust(await fetchMyOrganizationTrustStatus());
  }, []);

  const { refreshing, onRefresh: onRefreshProfile } = useScreenLoadShell(
    'Profile',
    async (opts) => {
      await Promise.all([refreshEntitlements(opts), refreshOrgTrust()]);
    }
  );

  useEffect(() => {
    if (!session?.user?.id) {
      setOrgTrust(null);
      return;
    }
    void refreshOrgTrust();
  }, [session?.user?.id, refreshOrgTrust]);

  const entitlementsLoadMissing = !entLoading && !entitlements && Boolean(session);
  const isOwner = entitlements?.is_owner === true;

  const [offlineOwnerHint, setOfflineOwnerHint] = useState<boolean | null>(null);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [closeBusinessModalOpen, setCloseBusinessModalOpen] = useState(false);
  const [closeBusinessReason, setCloseBusinessReason] = useState<CloseBusinessReason | null>(null);
  const [revokeEmployeeModalOpen, setRevokeEmployeeModalOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [logoutSubmitting, setLogoutSubmitting] = useState(false);

  useEffect(() => {
    const userId = session?.user?.id;
    if (!userId) {
      setOfflineOwnerHint(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const snap = await getOfflineAccountSnapshot(userId);
        if (cancelled) return;
        const fromSnap =
          snap?.entitlements?.is_owner === true ? true : snap?.entitlements?.is_owner === false ? false : null;
        setOfflineOwnerHint(fromSnap);
      } catch {
        if (!cancelled) setOfflineOwnerHint(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session?.user?.id]);

  const deletePolicyInput = useMemo<DeleteAccountPolicyInput>(
    () => ({ entLoading, entitlements, entitlementsLoadMissing, offlineOwnerHint }),
    [entLoading, entitlements, entitlementsLoadMissing, offlineOwnerHint],
  );
  const deleteAccountBlocked =
    resolveDeleteAccountBlocked(deletePolicyInput) || impersonating;

  const roleLabel = useMemo(() => {
    if (entLoading && !entitlements) return null;
    if (entitlementsLoadMissing) return 'No pudimos cargar tu rol';
    if (entitlements?.is_owner === true) return 'Dueño de la cuenta';
    if (isEmployeeRow === null) return null;
    if (isEmployeeRow === true) return 'Empleado';
    return 'Miembro del equipo';
  }, [entLoading, entitlements?.is_owner, isEmployeeRow, entitlementsLoadMissing, entitlements]);

  const accountEmail = session?.user?.email ?? null;

  // Nombre visible del dueño (customer-facing en turnos). Source: row
  // employees(role='owner') de la org activa; fallback user_metadata (Google).
  const metadataName = useMemo(() => {
    const meta = session?.user?.user_metadata as Record<string, unknown> | undefined;
    const raw = meta?.full_name ?? meta?.name;
    return typeof raw === 'string' ? raw.trim() : '';
  }, [session?.user?.user_metadata]);
  const [ownerRowName, setOwnerRowName] = useState<string | null>(null);
  const [displayNameModalOpen, setDisplayNameModalOpen] = useState(false);
  const [savingDisplayName, setSavingDisplayName] = useState(false);
  const displayName = ownerRowName ?? metadataName;

  const refreshDisplayName = useCallback(async () => {
    const userId = session?.user?.id;
    const orgId = entitlements?.organization_id;
    if (!userId || !orgId || entitlements?.is_owner !== true) {
      setOwnerRowName(null);
      return;
    }
    const { data } = await supabase
      .from('employees')
      .select('full_name')
      .eq('auth_user_id', userId)
      .eq('organization_id', orgId)
      .eq('role', 'owner')
      .maybeSingle();
    const name = typeof data?.full_name === 'string' ? data.full_name.trim() : '';
    setOwnerRowName(name || null);
  }, [session?.user?.id, entitlements?.organization_id, entitlements?.is_owner]);

  useEffect(() => {
    void refreshDisplayName();
  }, [refreshDisplayName]);

  const handleSaveDisplayName = useCallback(
    async (name: string) => {
      setSavingDisplayName(true);
      try {
        const { error } = await supabase.rpc('set_my_display_name', { p_name: name });
        if (error) throw error;
        // Sincroniza metadata para que el prefill de onboarding/portal quede al día.
        await supabase.auth.updateUser({ data: { full_name: name } }).catch(() => undefined);
        setOwnerRowName(name);
        setDisplayNameModalOpen(false);
        showSuccess('Nombre actualizado', `Tus clientes te ven como ${name}.`);
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : 'Probá de nuevo en unos minutos.';
        showError('No se pudo guardar tu nombre', message);
      } finally {
        setSavingDisplayName(false);
      }
    },
    [],
  );

  const organizationName = useMemo(() => {
    const fromEnt = typeof entitlements?.organization_name === 'string' ? entitlements.organization_name.trim() : '';
    if (fromEnt) return fromEnt;
    if (entitlementsLoadMissing && branches.length > 0) return 'Datos no disponibles';
    return null;
  }, [entitlements?.organization_name, entitlementsLoadMissing, branches.length]);

  const openActivityHistory = useCallback(() => navigateInShell(navigation, 'ActivityHistory'), [navigation]);
  const openGlossary = useCallback(() => navigateInShell(navigation, 'Glossary'), [navigation]);
  const openMarketplaceTrust = useCallback(
    () => navigateInShell(navigation, 'MarketplaceTrust'),
    [navigation],
  );

  const openOwnerHubPanel = useCallback(() => {
    void returnToPostLoginHubPanel(navigation);
  }, [navigation]);

  const marketplaceTrustDescription = useMemo(() => {
    if (!orgTrust) return 'Avisos y restricciones de la tienda online.';
    if (isTrustAttentionStatus(orgTrust.status)) {
      return trustStatusTitle(orgTrust.status);
    }
    // #1463: "Tu negocio anda bien" era vago — nombrar qué está bien.
    return 'Sin avisos ni restricciones en tu tienda online.';
  }, [orgTrust]);

  const marketplaceTrustNeedsAttention = Boolean(
    orgTrust && isTrustAttentionStatus(orgTrust.status),
  );

  const openCloseBusinessModal = useCallback(() => {
    if (deletingAccount || deleteAccountBlocked) return;
    setCloseBusinessReason(null);
    setCloseBusinessModalOpen(true);
  }, [deletingAccount, deleteAccountBlocked]);

  const openRevokeEmployeeModal = useCallback(() => {
    if (deletingAccount || deleteAccountBlocked) return;
    setRevokeEmployeeModalOpen(true);
  }, [deletingAccount, deleteAccountBlocked]);

  const handleConfirmCloseBusiness = useCallback(async () => {
    try {
      setDeletingAccount(true);
      await deleteMyAccount('close_business', {
        organizationId: entitlements?.organization_id ?? '',
        closeBusinessReason: closeBusinessReason ?? undefined,
      });
      setCloseBusinessModalOpen(false);
      showSuccess('Negocio cerrado', 'Tu negocio fue cerrado. Podés seguir comprando en el portal.');
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Probá de nuevo en unos minutos.';
      showError('No se pudo cerrar el negocio', message);
    } finally {
      setDeletingAccount(false);
    }
  }, [closeBusinessReason, entitlements?.organization_id]);

  const handleConfirmRevokeEmployee = useCallback(async () => {
    try {
      setDeletingAccount(true);
      await deleteMyAccount('revoke_employee', { organizationId: entitlements?.organization_id ?? '' });
      setRevokeEmployeeModalOpen(false);
      showSuccess('Acceso revocado', 'Tu acceso al negocio fue revocado. Podés seguir comprando en el portal.');
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Probá de nuevo en unos minutos.';
      showError('No se pudo revocar el acceso', message);
    } finally {
      setDeletingAccount(false);
    }
  }, [entitlements?.organization_id]);

  const handleConfirmLogout = useCallback(async () => {
    setLogoutSubmitting(true);
    try {
      await logout();
      setLogoutConfirmOpen(false);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Probá de nuevo en unos minutos.';
      showError('No se pudo cerrar sesión', message);
    } finally {
      setLogoutSubmitting(false);
    }
  }, []);

  return {
    userId: session?.user?.id ?? null,
    accountEmail,
    roleLabel,
    organizationName,
    isOwner,
    isEmployeeRow,
    displayName,
    displayNameModalOpen,
    savingDisplayName,
    setDisplayNameModalOpen,
    handleSaveDisplayName,
    isOrphan: !isOwner && isEmployeeRow === false && branches.length === 0,
    entLoading,
    deleteAccountBlocked,
    closeBusinessModalOpen,
    closeBusinessReason,
    setCloseBusinessReason,
    revokeEmployeeModalOpen,
    deletingAccount,
    openCloseBusinessModal,
    openRevokeEmployeeModal,
    handleConfirmCloseBusiness,
    handleConfirmRevokeEmployee,
    setCloseBusinessModalOpen,
    setRevokeEmployeeModalOpen,
    closeBusinessDescription: resolveCloseBusinessBlockedDescription(deleteAccountBlocked, entLoading),
    revokeEmployeeDescription: resolveRevokeEmployeeBlockedDescription(deleteAccountBlocked, entLoading),
    logoutConfirmOpen,
    logoutSubmitting,
    setLogoutConfirmOpen,
    handleConfirmLogout,
    refreshing,
    onRefreshProfile,
    openActivityHistory,
    openGlossary,
    openMarketplaceTrust,
    marketplaceTrustDescription,
    marketplaceTrustNeedsAttention,
    canAccessOwnerPanel,
    openOwnerHubPanel,
  };
}
